/**
 * Hook for real-time budget tracking with automatic transaction integration
 */

import { useMemo } from 'react';
import { useCashFlowEntries } from './useCashFlowEntries';
import { useIntegratedBudgets } from './useIntegratedBudgets';

interface CategoryBudgetStatus {
  categoryId: string;
  categoryName: string;
  budgeted: number;
  spent: number;
  remaining: number;
  percentUsed: number;
  status: 'safe' | 'warning' | 'danger' | 'over';
  isOverBudget: boolean;
}

interface BudgetTrackingData {
  totalBudgeted: number;
  totalSpent: number;
  totalRemaining: number;
  percentUsed: number;
  status: 'safe' | 'warning' | 'danger' | 'over';
  categories: CategoryBudgetStatus[];
  projectedEndOfMonth: number;
  daysRemaining: number;
  averageDailySpending: number;
}

export function useBudgetTracking(
  companyIds: string[],
  userId: string
): {
  data: BudgetTrackingData | null;
  loading: boolean;
  hasActiveBudget: boolean;
} {
  const { entries, loading: entriesLoading } = useCashFlowEntries(companyIds, userId);
  
  // Budget is per-company, so use the first selected company
  // Note: Multi-company budget tracking is not supported yet
  const firstCompanyId = companyIds && companyIds.length > 0 ? companyIds[0] : null;
  const { data: budgetData, isLoading: budgetsLoading } = useIntegratedBudgets(firstCompanyId);
  const budgets = budgetData?.budgets || [];

  const data = useMemo(() => {
    if (entriesLoading || budgetsLoading) return null;
    if (!budgets || budgets.length === 0) return null;

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const currentDay = now.getDate();
    const daysRemaining = daysInMonth - currentDay;

    // Get current month budget
    const activeBudget = budgets.find(b => {
      const startDate = new Date(b.start_date);
      const endDate = new Date(b.end_date);
      return now >= startDate && now <= endDate && b.is_active;
    });

    if (!activeBudget) return null;

    // Get current month transactions (confirmed only)
    const currentMonthEntries = entries.filter(entry => {
      // Use same date logic as display: payment_date > due_date > entry_date
      const relevantDate = new Date(entry.payment_date || entry.due_date || entry.entry_date || entry.created_at);
      return (
        relevantDate.getMonth() === currentMonth &&
        relevantDate.getFullYear() === currentYear &&
        entry.payment_status === 'confirmado' &&
        entry.entry_type === 'expense'
      );
    });

    // Calculate spending by category
    const categorySpending = new Map<string, number>();
    currentMonthEntries.forEach(entry => {
      const category = entry.main_category;
      categorySpending.set(
        category,
        (categorySpending.get(category) || 0) + entry.amount
      );
    });

    // Calculate category statuses
    const categories: CategoryBudgetStatus[] = [];
    let totalBudgeted = 0;
    let totalSpent = 0;

    activeBudget.items.forEach(item => {
      const spent = categorySpending.get(item.category_name || '') || 0;
      const budgeted = item.planned_amount;
      const remaining = budgeted - spent;
      const percentUsed = budgeted > 0 ? (spent / budgeted) * 100 : 0;

      let status: CategoryBudgetStatus['status'] = 'safe';
      if (percentUsed >= 100) status = 'over';
      else if (percentUsed >= 90) status = 'danger';
      else if (percentUsed >= 75) status = 'warning';

      categories.push({
        categoryId: item.category_id,
        categoryName: item.category_name || 'Sem categoria',
        budgeted,
        spent,
        remaining,
        percentUsed,
        status,
        isOverBudget: spent > budgeted,
      });

      totalBudgeted += budgeted;
      totalSpent += spent;
    });

    const totalRemaining = totalBudgeted - totalSpent;
    const percentUsed = totalBudgeted > 0 ? (totalSpent / totalBudgeted) * 100 : 0;

    let status: BudgetTrackingData['status'] = 'safe';
    if (percentUsed >= 100) status = 'over';
    else if (percentUsed >= 90) status = 'danger';
    else if (percentUsed >= 75) status = 'warning';

    // Calculate average daily spending and projection
    const averageDailySpending = totalSpent / currentDay;
    const projectedEndOfMonth = averageDailySpending * daysInMonth;

    return {
      totalBudgeted,
      totalSpent,
      totalRemaining,
      percentUsed,
      status,
      categories: categories.sort((a, b) => b.percentUsed - a.percentUsed),
      projectedEndOfMonth,
      daysRemaining,
      averageDailySpending,
    };
  }, [entries, budgets, entriesLoading, budgetsLoading]);

  return {
    data,
    loading: entriesLoading || budgetsLoading,
    hasActiveBudget: !!data,
  };
}
